import React, { useMemo } from "react";
import { Course } from "src/types/coaching/courses";
import { Media } from "src/types/media";
import Thumbnail from "src/components/thumbnail";
import DOMPurify from "dompurify";
import { FileService } from "src/services/file.service";
import Text from "src/components/text";

import _ from "lodash";
import cx from "classnames";
import styles from "./card.module.scss";
const defaultImage = "/images/collections/background.jpeg";
const defaultCourse = "/images/coaching/default_course.jpg";


interface CourseCardProps {
  course: Course;
  onClick: (course: Course) => void;
}

const CourseCard: React.FC<CourseCardProps> = ({
  course,
  onClick,
}) => {
  const name = useMemo(() => _.get(course, "name", ""), [course]);
  const description = useMemo(() => _.get(course, "description", ""), [course]);
  const icon = useMemo(
    () => _.get(course, "banner.background") as Media,
    [course],
  );

  return (
    <div
      className={cx(styles.card, styles.predefined)}
      onClick={() => onClick(course)}
    >
      <div className={cx(styles.header)}>
        <Thumbnail
          className={styles.icon}
          media={icon}
          defaultImage={defaultImage}
          width={600}
        />
        <img className={styles.callIcon} src="/images/coaching/course.png" />
      </div>
      <div className={styles.details}>
        <Text className={styles.name} text={name || ""} lines={1} />
        <div
          className={styles.description}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
        />
      </div>
      <div className={styles.footer}>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <i className="paperflite-document"></i>
            <span>{_.get(course, "lessons", 0)} lessons</span>
          </div>
          <div className={styles.divider}></div>
          <div className={styles.metadata}>
            <i className="paperflite-user-settings"></i>
            <span>created by {_.get(course, "createdBy.firstName", "")}</span>
          </div>
          <div className={styles.divider}></div>

          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span className={styles.value}>{FileService.formatDuration(_.get(course, "duration", 0), 'short')}</span>
          </div>
        </div>
        <div className={styles.actions}>
          <button className={cx(styles.action, styles.start)}>
            <i className="paperflite-right-arrow" />
            <span>explore learning</span>
          </button>
        </div>
      </div>
    </div>
  );
};

interface SimulationCardProps {
  course: any;
  onClick: (scenario: any) => void;
}

const SimulationCard: React.FC<SimulationCardProps> = ({
  course,
  onClick,
}) => {
  const name = useMemo(() => _.get(course, "name", ""), [course]);
  const description = useMemo(() => _.get(course, "description"), [course]);
  const duration = useMemo(
    () => _.get(course, "duration"),
    [course],
  );
  const industry = useMemo(
    () => _.get(course, "organization.industry") || "industry",
    [course],
  );

  const icon = useMemo(
    () => _.get(course, "banner.background") as Media,
    [course],
  );

  return (
    <div
      className={cx(styles.card, styles.predefined)}
      onClick={() => onClick(course.id)}
    >
      <div className={cx(styles.header)}>
        <Thumbnail
          className={styles.icon}
          media={icon}
          defaultImage={defaultCourse}
          width={600}
        />
        <img className={styles.callIcon} src="/images/coaching/call.png" />
      </div>
      <div className={styles.details}>
        <Text className={styles.name} text={name || ""} lines={1} />
        <div
          className={styles.description}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
        />
      </div>
      <div className={styles.footer}>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span className={styles.value}>{_.get(course, "duration", 0)}m</span>
          </div>
          <div className={styles.divider}></div>
          <div className={styles.metadata}>
         <i className="paperflite-user-settings"></i>
            <span>created by {_.get(course, "createdBy.firstName", "")}</span>
          </div>
          <div className={styles.divider}></div>

          <div className={styles.metadata}>
            <i className="paperflite-rocket"></i>
            <span>{industry}</span>
          </div>
        </div>
        <div className={styles.actions}>
          <button className={cx(styles.action, styles.start)}>
            <i className="paperflite-right-arrow" />
            <span>try now</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const PlaceholderSimulationCard: React.FC<object> = () => {
  return (
    <div className={cx(styles.simulationCard, styles.placeholder)}>
      <div className={cx(styles.header)}>
        <span className={cx(styles.day, styles.block)}></span>
        <span className={cx(styles.day, styles.block)}></span>
      </div>
      <div className={styles.body}>
        <div className={cx(styles.heading, styles.block)}></div>
        <p className={cx(styles.summary, styles.block)}></p>
      </div>

      <div className={styles.footer}>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
          <div className={styles.divider}></div>
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
          <div className={styles.divider}></div>
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PlaceholderCourseCard: React.FC = () => {
  return (
    <div className={cx(styles.card, styles.placeholder)}>
      <div className={cx(styles.header)}>
        <div className={cx(styles.icon, styles.block)}></div>
      </div>
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)}></div>
        <p className={cx(styles.description)}>
          <div className={styles.block}></div>
          <div className={styles.block} style={{ width: "65%" }}></div>
        </p>
      </div>
      <div className={styles.footer}>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
          <div className={styles.border} />
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
          <div className={styles.border} />
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ContinueLearningCardProps {
  course: Course;
  onClick: (course: Course) => void;
}

const ContinueLearningCard: React.FC<ContinueLearningCardProps> = ({
  course,
  onClick,
}) => {
  const name = useMemo(() => _.get(course, "course.name", ""), [course]);
  const description = useMemo(() => _.get(course, "course.description", ""), [course]);
  const icon = useMemo(
    () => _.get(course, "course.banner.background") as unknown as Media,
    [course],
  );

  const getProgress = (course: Course) => {
    const total = _.get(course, "course.duration", 0);
    const remaining = _.get(course, "renamingDuration", 0);
    
    if(remaining > 0) {
      const completed = ((total - remaining) / total) * 100;
      return `${Math.round(completed)}% completed`;
    }
    return "0% completed";
  };

  const progress = getProgress(course);

  return (
    <div
      className={cx(styles.card, styles.continue)}
      onClick={() => onClick(course)}
    >
      <div className={cx(styles.header)}>
        <Thumbnail
          className={styles.icon}
          media={icon}
          defaultImage={defaultImage}
          width={600}
        />
      </div>
      <div className={styles.details}>
        <Text className={styles.name} text={name || ""} lines={1} />
        <div className={styles.footer}>
          <div className={styles.metadatas}>
            <div className={styles.metadata}>
              <span>{progress}</span>
            </div>
          </div>

          <div className={styles.actions}>
            <button className={cx(styles.action, styles.start)}>
              <i className="paperflite-right-arrow" />
              <span>continue learning</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

const PlaceholderContinueLearningCard: React.FC = () => {
  return (
    <div className={cx(styles.card, styles.continuePlaceholder, styles.placeholder)}>
      <div className={cx(styles.header)}>
        <div className={cx(styles.icon, styles.block)}></div>
         <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={cx(styles.summary, styles.block)}></div>
         </div>
      </div>
    </div>
  );
};

interface PopularLearningCardProps {
  item: any;
  handleClick?: () => void;
}

const PopularLearningCard: React.FC<PopularLearningCardProps> = ({ item, handleClick }) => {
  const courseName = _.get(item.course, "name", "");
  const icon = _.get(item.course, "banner.background") as Media;

  return (
    <div className={cx(styles.card)} onClick={handleClick}>
      <div className={cx(styles.header)}>
        <div className={styles.icon}>
          <Thumbnail media={icon} defaultImage={defaultImage} width={370} />
        </div>
      </div>

      <div className={styles.content}>
        <Text className={styles.name} text={courseName || ""} lines={1} />

        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <span className={styles.label}>
              {_.capitalize(item?.type) || "Course"}
            </span>
          </div>
          <div className={styles.border} />
          <div className={styles.metadata}>
            <i className="paperflite-assessment"></i>
            <span
              className={styles.label}
            >{`${item?.enrolled || 0} users enrolled`}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export {
  SimulationCard,
  CourseCard,
  PopularLearningCard,
  ContinueLearningCard,
  PlaceholderSimulationCard,
  PlaceholderCourseCard,
  PlaceholderContinueLearningCard
};
